import { MasterKey } from '@overtur-monorepo/keying-hierarchy'
import { HttpClient } from '@angular/common/http'
import { ConditionalExpr } from '@angular/compiler'
import { Inject, Injectable } from '@angular/core'
import { KeyingAppEnvironmentConfig } from '../configuration/keying.app.environment.config'
import {
  Assignment,
  Bit,
  Block,
  Book,
  Column,
  KeyBitArray,
  KeySystem,
  Library,
  Page,
  ShearLines,
} from '../models/dev.test.models'
import { LoadingService } from './loading.service'
import { KeywayType } from '@overtur-monorepo/keying-shared'
import { KeyingDataSetsService } from './keying.data.sets.service'
import { take } from 'rxjs'
import { LoggingService } from './logging.service'

@Injectable({
  providedIn: 'root',
})
export class FormatDataService {
  constructor(
    private keyingAppEnvironmentConfig: KeyingAppEnvironmentConfig,
    @Inject(HttpClient) private httpClient: HttpClient,
    private loadingService: LoadingService,
    private keyingDataSetsService: KeyingDataSetsService,
    private loggingService: LoggingService
  ) {}

  masterKeyProfiles: Array<KeySystem> = []
  keywayType = KeywayType

  formatMatrix(rawMatrix) {
    let matrix = {
      sequenceOfProgression: rawMatrix.sequenceOfProgression.split(''),
      masterKeyBitting: rawMatrix.masterKeyBitting.split(''),
      mask: rawMatrix.mask.split(''),
      keyBitArray: rawMatrix.keyBitArray.map((b) => (b = b.split(''))),
      id: rawMatrix.id,
      keySystemId: rawMatrix.keySystemId,
    }

    matrix.keyBitArray = this.replaceWith(matrix.keyBitArray, '')

    return matrix
  }

  /* istanbul ignore next */
  formatLibrary(raw, masterKeys, isSaved = false) {
    this.loggingService.log('Formatting Library', raw)
    this.loadingService.set({ isLoading: true, message: `Formatting Library` })
    let library = new Library()
    library.stats.total = raw ? raw[0]?.totalCombinations : 0
    let count = 0
    // adding a flag variable to get rid of the spinner when the book loop is complete
    let isFormattingBooks = false

    // Books
    raw.forEach((rawBook) => {
      if (!isFormattingBooks) {
        isFormattingBooks = true
        this.loadingService.set({ isLoading: true, message: 'Formatting Book' })
      }
      count = count + 1
      let book = this.formatBook(rawBook, masterKeys, library, isSaved)
      library.books.push(book)
    })

    if (isFormattingBooks) {
      this.loadingService.set({ isLoading: false, message: `` })
    }
    this.loadingService.set({ isLoading: false, message: `` })

    this.formatAssignments(masterKeys, library)
    this.loggingService.log('Library Formatted', library)
    library.processing = false
    return library
  }

  replaceWith(input, key) {
    input.forEach((kba, kbaIndex) => {
      kba.forEach((bit, bitIndex) => {
        if (bit === '' || bit === 'S') {
          input[kbaIndex][bitIndex] = key
        }
      })
    })

    return input
  }

  /* istanbul ignore next */
  formatBook(rawBook, masterKeys, library, isSaved) {
    this.loggingService.log('rawBook', rawBook)
    let blockCounter = 0
    const book = new Book()
    book.name = rawBook.id
    book.id = rawBook.id
    book.stats.total = 0
    book.stats.assigned = 0
    book.stats.available = 0
    book.bookNumber = rawBook.bookNumber
    book.isSaved = isSaved
    book.isFormatted = true
    book.mask = rawBook.mask.split('')
    book.sequenceOfProgression = rawBook.sequenceOfProgression.split('')
    book.masterKeyBitting = rawBook.masterKeyBitting.split('')
    book.keyBitArray = rawBook.keyBitArray.map((b) => (b = b.split('')))
    book.keyBitArray = this.replaceWith(book.keyBitArray, '')
    book.keywayId = rawBook.keywayId
    book.splitCombineBittings = rawBook.splitCombineBittings
    book.splitBittings = rawBook.splitBittings
    book.combineBittings = rawBook.combineBittings

    // Page
    rawBook.pages.forEach((rawPage) => {
      const page = new Page()
      page.parent = book
      const columnsArray = []

      // Column
      rawPage.columns.forEach((rawColumn, colIndex) => {
        let blocksCount = rawPage.columns.length
        const column = new Column()
        column.parent = page
        column.number = colIndex

        // Block
        rawColumn.blocks.forEach((rawBlock, blockIndex) => {
          let block = new Block()
          block.id = 'block-' + blockCounter
          block.number = blockIndex
          block.parent = column
          blockCounter = blockCounter + 1

          ////////
          block = this.formatBittings(rawBlock, block, column, masterKeys, book, page, library)

          column.blocks.push(block)
          blocksCount--
        })

        columnsArray.push(column)
      })

      page.columns = columnsArray
      book.pages.push(page)
    })
    this.loadingService.set({ isLoading: false, message: '' })

    book.pages.forEach((bPage) => {
      Object.keys(bPage?.splitTracker).forEach((colNum) => {
        // this.loggingService.log('Column Number: ', colNum)

        Object.keys(bPage?.splitTracker[colNum]).forEach((blockNum) => {
          // this.loggingService.log('Block Number: ', blockNum)
          // this.loggingService.log("Result: ", bPage?.splitTracker[colNum][blockNum])

          book.pages.forEach((zPage) => {
            zPage.columns.forEach((zCol) => {
              zCol.blocks.forEach((zBlock) => {
                // Adding the check if all the block are not present
                if (bPage?.splitTracker[colNum][blockNum] && zCol.blocks[blockNum]) {
                  let emptyCounter = bPage.splitTracker[colNum][blockNum].length
                  let splitsCounter = zCol.blocks[blockNum].kbas.filter((kba) => kba.isASplit).length
                  let add = emptyCounter - splitsCounter

                  // console.log('emptyCounter', emptyCounter, 'splitsCounter', splitsCounter, 'add', add)

                  while (add > 0) {
                    let empty = new KeyBitArray()
                    empty.hidden = true
                    empty.number = emptyCounter
                    empty.isASplit = true
                    let adjustor = 2
                    if (zBlock?.children?.length) {
                      adjustor = splitsCounter
                    }

                    // Complicated solution that does not yet work
                    // zCol.blocks[blockNum].kbas.splice(zBlock.number, 0, empty)

                    // Simple solution that works
                    zCol.blocks[blockNum].kbas.push(empty)

                    add--
                  }
                }
              })
            })
          })
        })
      })
    })

    book.stats.total = rawBook.totalCombinations
    book.stats.assigned = rawBook.assignedCombinations
    book.stats.available = rawBook.availableCombinations
    this.loadingService.set({ isLoading: false, message: `` })
    this.loggingService.log('Library Book', book)
    return book
  }

  formatBittings(rawBlock, block, column, masterKeys, book, page, library) {
    // console.log('Format Bittings')

    // bittings
    rawBlock.bittings.forEach((rawKeyBitArray, kbaIndex) => {
      let kba = new KeyBitArray()
      kba.bittingErrors = rawKeyBitArray.bittingErrors
      kba.parent = block
      kba.masterKeyBitting = rawKeyBitArray.masterKeyBitting
      kba.assignedMasterKeyId = rawKeyBitArray.assignedMasterKeyId
      kba.splitCombineBittings = book.splitCombineBittings.find((sb) => sb.bitting === kba.masterKeyBitting)
      kba.level = 0
      kba.number = kbaIndex

      block.bittingsArray.push(rawKeyBitArray.masterKeyBitting)

      if (rawKeyBitArray.combinationBittingReservationTypeId) {
        kba.combinationBittingReservationTypeId = rawKeyBitArray.combinationBittingReservationTypeId
        kba.combinationBittingReservationType = {
          description: rawKeyBitArray.combinationBittingReservationType.description,
        }
        kba.combinationBittingReservationNotes = rawKeyBitArray.combinationBittingReservationNotes
      }

      this.setAssignments(kba, library, book, masterKeys)

      let splitKeyBitArray = rawKeyBitArray.masterKeyBitting.split('')
      splitKeyBitArray.forEach((rawbit) => {
        const bit = new Bit()
        bit.value = rawbit
        if (bit.value === 'S') {
          kba.hidden = true
        }
        kba.bits.push(bit)
      })

      this.auditKba(kba)
      block.kbas.push(kba)
      book.allBittings.push(kba)

      this.formatSplitBittings(library, kba, book, block, masterKeys)
      this.recursiveSplitBittings(book, kba, block, library, masterKeys, page, kba, kba.level)
      this.recursiveCombineBittings(book, kba, block, column, page)
    })

    block.kbas.map((bitting) => {
      // console.log('bitting.level', bitting.level)
      this.trackLines(bitting)
    })

    return block
  }

  recursiveSplitBittings(book, kba, block, library, masterKeys, page, patriarch, level) {
    if (
      book.splitCombineBittings.length &&
      book.splitCombineBittings.find((sb) => sb.bitting === kba.masterKeyBitting)
    ) {
      kba.splitCombineBittings = book.splitCombineBittings.find((sb) => sb.bitting === kba.masterKeyBitting)
      kba.children = kba.splitCombineBittings.kbas
      let newLevel = level + 1

      // console.log('kba.children', kba.children, 'Bitting: ', kba.masterKeyBitting)

      if (kba.children) {
        // kba.level = level + 1
        kba.splitCombineBittings.kbas.map((child, index) => {
          child.level = newLevel
          child.parent = block.kbas.find((bkba) => bkba.masterKeyBitting === kba.splitCombineBittings.bitting)
          this.auditKba(child)

          if (!page.splitTracker[block.parent.number]) {
            // Col Number
            page.splitTracker[block.parent.number] = {}
          }
          if (!page.splitTracker[block.parent.number][block.number]) {
            // Block Number
            page.splitTracker[block.parent.number][block.number] = []
          }
          // KBAs
          page.splitTracker[block.parent.number][block.number]?.push(patriarch)

          block.kbas.push(child)
          book.allBittings.push(child)
          // console.log('child', child)
          this.recursiveSplitBittings(book, child, block, library, masterKeys, page, patriarch, child.level)
        })
      }
    }
  }

  recursiveCombineBittings(book: Book, kba: KeyBitArray, block: Block, column: Column, page: Page) {
    book.splitCombineBittings.map((splitCombineObj) => {
      if (splitCombineObj.isCombine) {
        splitCombineObj.bittings.forEach((combinedBitting, index) => {
          if (kba.masterKeyBitting === combinedBitting) {
            kba.hidden = true
            kba.isCombine = true
            block.isCombine = true
            kba.combineType = splitCombineObj.combineType
            block.combineType = splitCombineObj.combineType
            column.combineType = splitCombineObj.combineType
            page.combineType = splitCombineObj.combineType

            if (
              index === 0 &&
              !block.kbas.find((k) => k.masterKeyBitting === splitCombineObj.kbas[0].masterKeyBitting)
            ) {
              splitCombineObj.kbas[0].combineType = splitCombineObj.combineType
              this.auditKba(splitCombineObj.kbas[0])
              page.undoCombination = splitCombineObj.kbas[0].masterKeyBitting
              column.undoCombination = splitCombineObj.kbas[0].masterKeyBitting
              block.undoCombination = splitCombineObj.kbas[0].masterKeyBitting
              block.kbas.splice(kba.number, 0, splitCombineObj.kbas[0])
              block.kbas.splice(kba.number + 1, 1)
              block.kbas[0].parent = block
              book.allBittings.push(block.kbas[0])
            }
          }
        })
      }
    })
  }

  formatSplitBittings(library, parent, book, block, masterKeys) {
    if (book?.splitCombineBittings) {
      // Split
      book.splitCombineBittings.map((splitCombineObject) => {
        // console.log('splitCombineObject', splitCombineObject)

        splitCombineObject.kbas = []

        if (splitCombineObject.isSplit) {
          splitCombineObject.bittings.map((rsb) => {
            let found = book.splitBittings.find((bitting) => bitting.masterKeyBitting === rsb)

            let kba = new KeyBitArray()
            kba.isASplit = true
            kba.masterKeyBitting = rsb
            kba.bittingErrors = found.bittingErrors
            kba.masterKeyBitting = found.masterKeyBitting
            kba.assignedMasterKeyId = found.assignedMasterKeyId

            if (found.combinationBittingReservationTypeId) {
              kba.combinationBittingReservationTypeId = found.combinationBittingReservationTypeId
              kba.combinationBittingReservationType = {
                description: found.combinationBittingReservationType.description,
              }
              kba.combinationBittingReservationNotes = found.combinationBittingReservationNotes
            }

            this.setAssignments(kba, library, book, masterKeys)

            let bittingArray = rsb.split('')
            bittingArray.forEach((rawbit) => {
              const bit = new Bit()
              bit.value = rawbit
              if (bit.value === 'S') {
                kba.hidden = true
              }
              kba.bits.push(bit)
            })
            splitCombineObject.kbas.push(kba)
          })

          // Combine
        } else if (splitCombineObject.isCombine) {
          // console.log('isCombine', splitCombineObject)
          // console.log('splitCombineObject.bitting', splitCombineObject.bitting)
          let found = book.combineBittings.find((bitting) => bitting.masterKeyBitting === splitCombineObject.bitting)

          let kba = new KeyBitArray()
          kba.isCombine = true
          kba.masterKeyBitting = splitCombineObject.bitting
          kba.bittingErrors = found.bittingErrors
          kba.masterKeyBitting = found.masterKeyBitting
          kba.assignedMasterKeyId = found.assignedMasterKeyId

          if (found.combinationBittingReservationTypeId) {
            kba.combinationBittingReservationTypeId = found.combinationBittingReservationTypeId
            kba.combinationBittingReservationType = {
              description: found.combinationBittingReservationType.description,
            }
            kba.combinationBittingReservationNotes = found.combinationBittingReservationNotes
          }

          this.setAssignments(kba, library, book, masterKeys)

          let bittingArray = splitCombineObject.bitting.split('')
          bittingArray.forEach((rawbit) => {
            const bit = new Bit()
            bit.value = rawbit
            if (bit.value === 'S') {
              kba.hidden = true
            }
            kba.bits.push(bit)
          })
          splitCombineObject.kbas.push(kba)
        }
      })
    }
  }

  trackLines(kba: KeyBitArray) {
    if (kba.parent && kba.parent.children) {
      // console.log('Level: ', kba.level, kba.parent.children.length)
      kba.parent.children.forEach((child, index) => {
        // console.log('index', index)

        if (index === kba.parent.children.length - 1 && child.masterKeyBitting === kba.masterKeyBitting) {
          kba.lineTracker[kba.level] = { key: kba.masterKeyBitting, value: true }
        } else {
          kba.lineTracker[kba.level] = { key: kba.masterKeyBitting, value: false }
        }
        // console.log('kba.lineTracker', kba.lineTracker)
      })
    }

    if (kba.children) {
      kba.children.forEach((child, index) => {
        Object.keys(kba.lineTracker).forEach((line) => {
          child.lineTracker[line] = kba.lineTracker[line]
        })

        if (kba.lineTracker[kba.level]) {
          child.lineTracker[kba.level] = kba.lineTracker[kba.level]
        }

        this.trackLines(child)
      })
    }
  }

  setAssignments(kba, library, book, masterKeys) {
    let filtered = masterKeys.children.find((k) => k.id === kba.assignedMasterKeyId)
    if (filtered && filtered.id) {
      kba.assignment = new Assignment()

      let mk = masterKeys.children.find((k) => k.id === kba.assignedMasterKeyId)
      mk.assignment.kba = kba
      kba.assignment.masterKey = mk

      if (!book.stats.assignments.find((assignment) => assignment.id === mk.id)) {
        book.stats.assignments.push(mk)

        let assignment = {
          bitting: kba,
          masterKey: mk,
        }
        library.stats.assignments.push(assignment)
      }
    }

    if (kba.combinationBittingReservationTypeId) {
      kba.assignment = new Assignment()

      kba.assignment.reserved = true
      kba.assignment.reservationType.id = kba.combinationBittingReservationTypeId
      kba.assignment.reservationType.description = kba.combinationBittingReservationType.description
      kba.assignment.reservationNote = kba.combinationBittingReservationNotes

      let assignment = {
        bitting: kba,
        reservationType: kba.assignment.reservationType,
        reservationNote: kba.assignment.reservationNote,
      }

      if (!kba.assignment) {
        kba.assignment = new Assignment()
      }
      library.stats.assignments.push(assignment)
    }
  }

  formatShearLines(rawShearLines, combinatingRules) {
    let formattedShearLines = new ShearLines()

    formattedShearLines.id = rawShearLines.id
    formattedShearLines.length = rawShearLines.length
    formattedShearLines.masterKeyCylinderId = rawShearLines.masterKeyCylinderId
    formattedShearLines.shearLineDepths = []
    formattedShearLines.chambers = rawShearLines.chambers
    if (rawShearLines.errors) {
      formattedShearLines.errors = rawShearLines.errors
    } else {
      formattedShearLines.errors = []
    }

    rawShearLines.shearLineDepths.forEach((rawShearLine) => {
      let formattedShearLine

      formattedShearLine = rawShearLine.split('')

      formattedShearLines.shearLineDepths.push(formattedShearLine)
    })

    formattedShearLines = this.auditShearLines(formattedShearLines, combinatingRules)

    // console.log('combinatingRules', combinatingRules)
    // console.log('formattedShearLines', formattedShearLines)
    return formattedShearLines
  }

  auditShearLines(shearLines, combinatingRules) {
    shearLines.chambers.forEach((chamber, index) => {
      if (
        index === 0 &&
        combinatingRules.requirePinningInFirstChamber.isApplicable &&
        combinatingRules.requirePinningInFirstChamber.isRule
      ) {
        chamber.canEmpty = false
      }

      shearLines.shearLineDepths.forEach((row) => {
        if (chamber.isEmpty) {
          chamber.isEmpty = true
          chamber.canEmpty = true
        }
      })

      let numEmpty = shearLines.chambers.filter((c) => c.isEmpty === true)
      let numCanEmpty = shearLines.chambers.length - combinatingRules.minimumNumberOfPinnedChambers.value
      if (numEmpty.length === numCanEmpty) {
        if (!chamber.isEmpty) {
          chamber.canEmpty = false
        }
      } else {
        if (!chamber.isEmpty && index !== 0) {
          chamber.canEmpty = true
        }
      }
    })

    return shearLines
  }

  formatCylinders(rawCylinders) {
    this.loggingService.log('rawCylinders', rawCylinders)

    return rawCylinders
  }

  assignBittings(masterKeys, selectedKbaArray, selectedBook, rules) {
    this.keyingDataSetsService.structureDataSets$.pipe(take(1)).subscribe((dataSets) => {
      const keyway = dataSets.keyways.find((k) => k.id === selectedBook.keywayId)

      masterKeys.forEach((masterKey) => {
        if (
          (masterKey.masterKeyType?.name === 'Master' ||
            (masterKey.masterKeyType?.name === 'DirectChangeMaster' &&
              keyway.keywayTypeId !== this.keywayType.Elevated) ||
            masterKey.masterKeyType?.name === 'Direct' ||
            (masterKey.masterKeyType?.name === 'SingleKeyDifferentMaster' &&
              keyway.keywayTypeId !== this.keywayType.Elevated) ||
            masterKey.masterKeyType?.name === 'SingleKeyDifferent' ||
            masterKey.masterKeyType?.name === 'ControlKey' ||
            masterKey.masterKeyType?.name === 'ChangeKey' ||
            masterKey.masterKeyType?.name === 'SelectiveMaster' ||
            masterKey.masterKeyType?.name === 'ControlKeyMaster') &&
          (!masterKey.assignment || (masterKey.assignment && !masterKey.assignment.kba && !masterKey.isAssignedLocally))
        ) {
          let found = false

          selectedKbaArray.forEach((bittings) => {
            if (
              !found &&
              !bittings?.assignment?.masterKey?.id &&
              !bittings.hasError &&
              (!bittings.hasWarning || !rules.skipGuidelineViolators) &&
              (!masterKey.isAssigned || masterKey.unassignedLocally) &&
              !bittings.assignment.reserved &&
              !bittings.children
            ) {
              masterKey.assignment = new Assignment()
              masterKey.assignment.kba = bittings
              bittings.assignment = new Assignment()
              bittings.assignment.masterKey = masterKey
              found = true
            }
          })
        }
      })
      this.loggingService.log('assignBittings', selectedKbaArray)
    })
  }

  prepareAssignmentRequest(book) {
    let changes = []
    // Book
    book.pages.forEach((page) => {
      // Pages
      page.columns.forEach((column) => {
        // Block
        column.blocks.forEach((block) => {
          // bittings
          block.kbas.forEach((bitting) => {
            if (bitting.masterKeyBitting) {
              if (bitting.assignment && bitting.assignment.masterKey) {
                let request = {
                  bitting: bitting.flatBits(),
                  masterKeyId: bitting.assignment.masterKey.id,
                }
                changes.push(request)
              } else if (
                bitting.assignment &&
                !bitting.assignment.masterKey &&
                !bitting?.assignment?.reservationType?.id
              ) {
                let request = {
                  bitting: bitting.flatBits(),
                  masterKeyId: null,
                  CombinationBittingReservationTypeId: null,
                }
                changes.push(request)
              } else if (bitting.assignment && bitting.assignment.reservationType.id) {
                let request = {
                  bitting: bitting.flatBits(),
                  CombinationBittingReservationTypeId: bitting.assignment.reservationType.id,
                  CombinationBittingReservationNotes: bitting.assignment.reservationNote,
                  description: bitting.assignment.reservationType.description,
                }
                changes.push(request)
              }
            }
          })
        })
      })
    })
    return changes
  }

  mergeLibraries(libraryOne, libraryTwo) {
    libraryOne.books.forEach((book) => {
      libraryTwo.books.push(book)
    })
    // this.loggingService.log('Merged Library', libraryTwo)
    return libraryTwo
  }

  auditKba(kba) {
    kba.bits.forEach((bit, bitIndex) => {
      if (kba.bittingErrors) {
        kba.bittingErrors.forEach((error) => {
          if (bitIndex >= error.beginningChamber && bitIndex <= error.endingChamber) {
            if (error.isRule) {
              kba.hasError = true
              bit.hasError = true
              bit.error = error.errorMessage
            } else {
              kba.hasWarning = true
              bit.hasWarning = true
              bit.warning = error.errorMessage
            }
          }
        })
      }
    })
    return kba
  }

  sortHierarchically(subject, flat) {
    let mapped = flat.map((s) => s.keySetSymbol)

    subject = subject.sort((a, b) => {
      return mapped.indexOf(a.keySetSymbol) - mapped.indexOf(b.keySetSymbol)
    })

    return subject
  }

  /*istanbul ignore next */
  formatAssignments(selectedMasterKey, library) {
    let flat = selectedMasterKey.squashIncludeChangeKeys()

    library.books.map((book) => {
      if (book.stats.assignments.length) {
        this.sortHierarchically(book.stats.assignments, flat)
        // this.throughNotation(book.stats)
        this.getKeys(book.stats)
        let includedList = []
        includedList = book.stats.thruNodes.filter((assignment) => !assignment.inThruNode)
        includedList = includedList.map((i) => (i = i.keySetSymbol))
        book.assignmentString = includedList.join(', ')
      }
    })
  }

  /*istanbul ignore next */
  getKeys(stats) {
    stats.keys = stats.assignments.filter((k) => k.masterKeyTypeId !== 99 && k.masterKeyTypeId !== 100)
    stats.keys = this.sortHierarchically(
      stats.keys,
      stats.assignments.map((s) => s.keySetSymbol)
    )
    return (stats.thruNodes = stats.assignments)
  }

  throughNotation(stats) {
    stats.thruNodes = stats.assignments

    // Filter by type and assignment
    let assignedChangeKeys = stats.thruNodes.filter(
      (c) => c.isAssigned && (c.masterKeyTypeId === 8 || c.masterKeyTypeId === 10)
    )
    assignedChangeKeys.sort((a, b) => a.keySetSymbol.length - b.keySetSymbol.length)
    let unassignedChangeKeys = stats.thruNodes.filter(
      (c) => !c.isAssigned && (c.masterKeyTypeId === 8 || c.masterKeyTypeId === 10)
    )
    unassignedChangeKeys.sort((a, b) => a.keySetSymbol.length - b.keySetSymbol.length)
    let assignedSkdKeys = stats.thruNodes.filter((c) => c.isAssigned && c.masterKeyTypeId === 6)
    assignedSkdKeys.sort((a, b) => a.keySetSymbol.length - b.keySetSymbol.length)
    let unassignedSkdKeys = stats.thruNodes.filter((c) => !c.isAssigned && c.masterKeyTypeId === 6)
    unassignedSkdKeys.sort((a, b) => a.keySetSymbol.length - b.keySetSymbol.length)

    // Split by Key Set Symbol
    let assignedChangeSplitNodes = this.splitNodes(assignedChangeKeys)
    let unassignedChangeSplitNodes = this.splitNodes(unassignedChangeKeys)
    let assignedSkdSplitNodes = this.splitNodes(assignedSkdKeys)
    let unassignedSkdSplitNodes = this.splitNodes(unassignedSkdKeys)

    // Calculate Thru Nodes
    assignedChangeSplitNodes.forEach((node) => this.calcNodes(stats, node, 99, true))
    unassignedChangeSplitNodes.forEach((node) => this.calcNodes(stats, node, 99))
    assignedSkdSplitNodes.forEach((node) => this.calcNodes(stats, node, 100, true))
    unassignedSkdSplitNodes.forEach((node) => this.calcNodes(stats, node, 100))

    this.sortThruNodes(stats.thruNodes)
    // return stats.thruNodes
  }

  sortThruNodes(thruNodes) {
    let sorted = thruNodes
      .filter((k) => k.masterKeyTypeId === 99 || k.masterKeyTypeId === 100 || k.masterKeyTypeId === 101)
      .sort(
        (a, b) => a.thruKeys[0].keySetSymbol.replace(/[^0-9]/g, '') - b.thruKeys[0].keySetSymbol.replace(/[^0-9]/g, '')
      )

    sorted.forEach((sortedKey) => {
      if (thruNodes.filter((si) => si.id === sortedKey.id).length === 1) {
        let fromIndex = thruNodes.findIndex((si) => si.id === sortedKey.id)
        thruNodes = this.arrayMove(thruNodes, fromIndex, thruNodes.length)
      }
    })

    return thruNodes
  }

  arrayMove(array, fromIndex, toIndex) {
    array.splice(toIndex, 0, array.splice(fromIndex, 1)[0])
    return array
  }

  splitNodes(keys) {
    let nodes = {}
    keys.forEach((key) => {
      let letters = key.keySetSymbol.replace(/[^A-Z]/g, '')
      if (!nodes[letters]) {
        nodes[letters] = []
      }
      nodes[letters].push(key)
    })
    return Object.values(nodes)
  }

  calcNodes(stats, node, type, assigned = false) {
    let startingIndex = 0
    let endingIndex = 0
    node.forEach((key, index) => {
      let dif
      if (index > 0) {
        dif =
          Number(key.keySetSymbol.replace(/[^0-9]/g, '')) - Number(node[index - 1].keySetSymbol.replace(/[^0-9]/g, ''))
      }
      if (index > 0 && dif > 1) {
        endingIndex = index - 1
        this.createThruNode(stats, node, startingIndex, endingIndex, type, assigned)
        startingIndex = index
      }
      if (index + 1 === node.length) {
        endingIndex = index
        this.createThruNode(stats, node, startingIndex, endingIndex, type, assigned)
      }
    })
  }

  createThruNode(stats, keys, startingIndex, endingIndex, type, assigned) {
    let thruNode = new MasterKey()
    thruNode.masterKeyTypeId = type

    if (keys.length > 0 && stats.thruNodes.masterKeyTypeId !== type) {
      let firstEle = keys[startingIndex]
      let lastEle = keys[endingIndex]

      thruNode.id = Number(firstEle.id.toString() + lastEle.id.toString() + type)

      if (firstEle.keySetSymbol === lastEle.keySetSymbol) {
        thruNode.keySetSymbol = `${firstEle.keySetSymbol}`
      } else {
        thruNode.keySetSymbol = `${firstEle.keySetSymbol}-${lastEle.keySetSymbol}`
      }

      thruNode.thruKeys = []

      keys.forEach((key, index) => {
        if (index >= startingIndex && index <= endingIndex) {
          key.inThruNode = true
          thruNode.thruKeys.push(key)
        }
      })

      if (assigned) {
        thruNode.isAssigned = true
      }

      if (!stats.thruNodes.some((c) => c.id === thruNode.id)) {
        stats.thruNodes.push(thruNode)
      }
    }
  }
}
